import { useState, useMemo } from "react";
import { formatMomentDate } from "../../../utils/dateHelper";
import { PlusIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import StatusChip from "../../../components/statusChip/StatusChip";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";

const ListSection = ({
  data,
  isLoading,
  searchTerm,
  setSearchTerm,
  onAdd,
  onEdit,
  onView,
  onDelete,
  onManagePermissions,
}) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const filteredData =
    data?.filter(
      (role) =>
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.slug.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const handleConfirmDeactivate = () => {
    if (selectedRole && typeof onDelete === "function") {
      onDelete(selectedRole);
      setShowStatusModal(false);
      setSelectedRole(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "sr_no",
        header: "SR NO.",
        cell: (_, row, index) => index + 1,
      },
      {
        id: "name",
        header: "ROLE NAME",
        cell: (_, row) => row.name,
      },
      {
        id: "slug",
        header: "SLUG",
        cell: (_, row) => (
          <code className="bg-light px-2 py-1 rounded text-dark">
            {row.slug}
          </code>
        ),
      },
      {
        id: "status",
        header: "STATUS",
        cell: (_, row) => (
          <StatusChip
            bg={row.is_active ? "success" : "danger"}
            pill
            title={row.is_active ? "Active" : "Inactive"}
            className={row.is_system || !onDelete ? "" : "cursor-pointer"}
            onClick={() => {
              if (!row.is_system && onDelete) {
                setSelectedRole(row);
                setShowStatusModal(true);
              }
            }}
          />
        ),
      },
      {
        id: "created_at",
        header: "CREATED ON",
        cell: (_, row) =>
          row.createdAt ? formatMomentDate(row.createdAt, "DD-MM-YYYY hh:mm A") : "-",
      },
      {
        id: "updated_at",
        header: "UPDATED ON",
        cell: (_, row) =>
          row.updatedAt ? formatMomentDate(row.updatedAt, "DD-MM-YYYY hh:mm A") : "-",
      },
    ],
    [onDelete],
  );

  const actionList = useMemo(
    () =>
      [
        onView && {
          title: "View",
          handler: (row) => onView(row),
        },
        onEdit && {
          title: "Edit",
          isVisible: (row) => !row.is_system,
          handler: (row) => onEdit(row),
        },
        onManagePermissions && {
          title: "Manage Permissions",
          isVisible: (row) => !row.is_system,
          handler: (row) => onManagePermissions(row),
        },
        onDelete && {
          title: (row) => (row.is_active ? "Deactivate" : "Activate"),
          isVisible: (row) => !row.is_system,
          handler: (row) => {
            setSelectedRole(row);
            setShowStatusModal(true);
          },
        },
      ].filter(Boolean),
    [onView, onEdit, onManagePermissions, onDelete],
  );

  return (
    <>
      <div className="p-4 d-flex align-items-center justify-content-between gap-4">
        <TableSearch
          value={searchTerm}
          placeholder="Search"
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {onAdd && (
          <CommonButton
            variant="primary"
            icon={<PlusIcon />}
            title="Add Role"
            type="button"
            onClick={onAdd}
          />
        )}
      </div>

      <CustomTable
        data={filteredData}
        columns={columns}
        loading={isLoading}
        keyField="id"
        emptyMessage="No roles found"
        actionList={actionList}
        isRowActionDisabled={(row) => row.is_system}
      />

      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedRole(null);
        }}
        size="sm"
        title={selectedRole?.is_active ? "Deactivate Role" : "Activate Role"}
        message={`Are you sure you want to ${selectedRole?.is_active ? "deactivate" : "activate"} this role?`}
        onConfirm={handleConfirmDeactivate}
      />
    </>
  );
};

export default ListSection;
